import { createApi } from '@reduxjs/toolkit/query/react';
import { createSelector } from '@reduxjs/toolkit';
import { baseApi, fetchbase } from '@centerpiece/components';
import { RevenueForecastChartData, WinRates } from './models';
import { generateRevenueForecastChartData } from 'utils/analytics.utils';

export type OverviewAnalyticsQueryParams = {
  team?: number;
  type?: number;
  concept?: object[];
  individual?: number;
  segment?: object[];
};

export type PerformanceAnalyticsQueryParams = {
  team?: number;
  type?: object[];
  concept?: object[];
  individual?: number;
  segment?: object[];
};

export const analytic = baseApi
  .enhanceEndpoints({ addTagTypes: ['Analytics', 'Analytic'] })
  .injectEndpoints({
    // reducerPath: 'analytic',
    // // global configuration for the api
    // keepUnusedDataFor: 120, //keep unused data in cache for 2 minutes
    // refetchOnFocus: true,
    // refetchOnMountOrArgChange: true,
    // tagTypes: ['Analytics', 'Analytic'],
    // baseQuery: fetchbase,
    endpoints: builder => ({
      getFactorAnalytics: builder.query<any, { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: '/analytic/factor',
          params: params,
        }),
        providesTags: (result, error, arg) => [
          { type: 'Analytic' as const, id: 'volume' },
        ],
      }),
      getPricingAnalytics: builder.query<any, { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: '/analytic/pricing',
          params: params,
        }),
        providesTags: (result, error, arg) => [
          { type: 'Analytic' as const, id: 'pricing' },
        ],
      }),
      getPerformanceAnalytics: builder.query<any, { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: '/analytic/performance',
          params: params,
        }),
        providesTags: (result, error, arg) => [
          { type: 'Analytic' as const, id: 'performance' },
        ],
      }),
      getRealizeRevenueAnalytics: builder.query<any, { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: '/analytic/realized_revenue',
          params: params,
        }),
        providesTags: (result, error, arg) => [
          { type: 'Analytic' as const, id: 'realized_revenue' },
        ],
      }),
      getForecastAnalytics: builder.query<any, { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: '/analytic/forecast',
          params: params,
        }),
        providesTags: (result, error, arg) => [
          { type: 'Analytic' as const, id: 'forecast' },
        ],
      }),
      getAwardedAnalytics: builder.query<any, { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: '/analytic/awarded',
          params: params,
        }),
        // transformResponse: (data: IKpi, meta, arg) => {
        //   return Array.isArray(data) && data.length > 0 ? data[0] : data;
        // },
        providesTags: (result, error, arg) => [
          { type: 'Analytic' as const, id: 'awarded' },
        ],
      }),
      getUpliftAnalytics: builder.query<any, { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: '/analytic/uplift',
          params: params,
        }),
        // transformResponse: (data: IKpi, meta, arg) => {
        //   return Array.isArray(data) && data.length > 0 ? data[0] : data;
        // },
        providesTags: (result, error, arg) => [
          { type: 'Analytic' as const, id: 'uplift' },
        ],
      }),
      getWinLossAnalytics: builder.query<any, { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: '/analytic/winloss',
          params: params,
        }),
        // transformResponse: (data: IKpi, meta, arg) => {
        //   return Array.isArray(data) && data.length > 0 ? data[0] : data;
        // },
        providesTags: (result, error, arg) => [
          { type: 'Analytic' as const, id: 'winloss' },
        ],
      }),
      getLossReasonAnalytics: builder.query<any, { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: '/analytic/loss_reason',
          params: params,
        }),
        // transformResponse: (data: IKpi, meta, arg) => {
        //   return Array.isArray(data) && data.length > 0 ? data[0] : data;
        // },
        providesTags: (result, error, arg) => [
          { type: 'Analytic' as const, id: 'loss_reason' },
        ],
      }),
      getWinRateAnalytics: builder.query<Array<WinRates>, { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: '/analytic/win_rate',
          params: params,
        }),
        providesTags: (result, error, arg) => [
          { type: 'Analytic' as const, id: 'win_rate' },
        ],
      }),
    }),
  });

// Auto-generated hooks
export const {
  usePrefetch: useAnalyticsPrefetch,
  useGetUpliftAnalyticsQuery,
  useGetFactorAnalyticsQuery,
  useGetForecastAnalyticsQuery,
  useGetAwardedAnalyticsQuery,
  useGetWinLossAnalyticsQuery,
  useGetPerformanceAnalyticsQuery,
  useGetRealizeRevenueAnalyticsQuery,
  useGetPricingAnalyticsQuery,
  useGetWinRateAnalyticsQuery,
  useGetLossReasonAnalyticsQuery,
} = analytic;

const selectFactorAnalyticsRoot = analytic.endpoints.getFactorAnalytics.select(
  {}
);
export const selectFactorAnalytics = createSelector(
  selectFactorAnalyticsRoot,
  analytic => analytic.data || []
);
const selectPricingAnalyticsRoot =
  analytic.endpoints.getPricingAnalytics.select({});
export const selectPricingAnalytics = createSelector(
  selectPricingAnalyticsRoot,
  analytic => analytic.data || []
);
const selectForecastAnalyticsRoot =
  analytic.endpoints.getForecastAnalytics.select({});
export const selectForecastAnalytics = createSelector(
  selectForecastAnalyticsRoot,
  analytic => analytic.data || []
);
const selectAwardedAnalyticsRoot =
  analytic.endpoints.getAwardedAnalytics.select({});
export const selectAwardedAnalytics = createSelector(
  selectAwardedAnalyticsRoot,
  analytic => analytic.data || []
);
const selectWinRateAnalyticsRoot =
  analytic.endpoints.getWinRateAnalytics.select({});
export const selectWinRateAnalytics = createSelector(
  selectWinRateAnalyticsRoot,
  analytic => analytic.data || []
);
